import { type ChangeEvent, useEffect, useState } from "react";
import { Popover } from "radix-ui";
import { Lock, LockOpen, MessageSquare } from "lucide-react";
import { MUTATIONS, useMutation } from "../../../api";
import type { WorkshopId } from "../../../api/workshop";
import Button from "../../../design-system/components/Button/Button.jsx";
import Icon from "../../../design-system/components/Icon/Icon.jsx";
import { Variants } from "../../../design-system/components/Button/Variants.jsx";
import Dialog from "../../../design-system/components/Dialog/Dialog.jsx";
import Textarea from "../../../design-system/components/Textarea/Textarea.jsx";
import Text from "../../../design-system/primitives/components/Text/Text";
import toolbarStyles from "../../../design-system/components/Toolbar/toolbar.css";
import styles from "./classroomManagement.css";
import { useLiveState } from "./hooks/useLiveState";

/** How long the "Nachricht gesendet" confirmation stays up before auto-closing. */
const SENT_CONFIRMATION_MS = 2000;

/**
 * Teacher/admin toolbar entry for classroom management. Opens a popover with the
 * available live actions (pause the class / show a message).
 *
 * The trigger shows its active state while a pause is live, so teachers can see
 * at a glance that screens are locked. A message is fire-and-forget — there's no
 * lasting state to reflect — so it doesn't drive the active state.
 *
 * "Pausieren" opens the pause dialog — the same dialog serves both states,
 * showing a confirm-to-start prompt when no pause is live and a "Fortsetzen"
 * (resume) prompt once one is. Starting a pause deliberately closes the dialog
 * rather than flipping it to the resume prompt: the teacher re-opens it here when
 * they want to resume. "Nachricht" opens a compose dialog that broadcasts a
 * one-off message (a brief "gesendet" confirmation, then nothing to manage). All
 * act via the `manageWorkshop` mutation; the new live state comes back over SSE.
 */
export const ClassroomManagementButton = ({
  workshopId,
}: {
  workshopId: WorkshopId;
}) => {
  const [open, setOpen] = useState(false);
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageText, setMessageText] = useState("");
  // After sending, the send button flips to a green "gesendet" state (there's no
  // lasting message to manage) and the dialog auto-closes shortly after.
  const [messageSent, setMessageSent] = useState(false);
  const { pause } = useLiveState(workshopId);
  const [manageWorkshop] = useMutation(MUTATIONS.manageWorkshop);

  useEffect(() => {
    if (!messageSent) return;
    const timer = setTimeout(() => {
      setMessageDialogOpen(false);
      setMessageSent(false);
    }, SENT_CONFIRMATION_MS);
    return () => clearTimeout(timer);
  }, [messageSent]);

  const openPauseDialog = () => {
    setOpen(false);
    // One dialog for both states — it shows the confirm-to-start or the resume
    // prompt depending on whether a pause is currently live.
    setPauseDialogOpen(true);
  };

  const openMessageDialog = () => {
    setOpen(false);
    // Always compose a fresh one-off — a message is fire-and-forget, so there's
    // no "active" message to re-open.
    setMessageText("");
    setMessageSent(false);
    setMessageDialogOpen(true);
  };

  const startPause = () => {
    // Close rather than flip to the resume prompt — the teacher re-opens it from
    // here when they want to resume.
    setPauseDialogOpen(false);
    void manageWorkshop({ workshopId, action: { action: "startPause" } });
  };

  const endPause = () => {
    setPauseDialogOpen(false);
    void manageWorkshop({ workshopId, action: { action: "endPause" } });
  };

  const sendMessage = () => {
    const content = messageText.trim();
    if (!content) return;
    void manageWorkshop({
      workshopId,
      action: { action: "showMessage", payload: { content } },
    });
    // Fire-and-forget: show the confirmation; the effect above closes the dialog.
    setMessageSent(true);
  };

  return (
    <>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            id="classroom-button"
            type="button"
            className={toolbarStyles.toolbarButton({
              active: Boolean(pause),
              wide: true,
            })}
          >
            <Icon name="handWave" size="32px" />
            Klassenzimmer
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          {/* No arrow, per design. */}
          <Popover.Content className={styles.popoverContent} sideOffset={8}>
            <button
              type="button"
              className={styles.menuItem}
              data-active={Boolean(pause)}
              onClick={openPauseDialog}
            >
              {pause ? <Lock size={24} /> : <LockOpen size={24} />}
              Pausieren
            </button>
            <button
              type="button"
              className={styles.menuItem}
              onClick={openMessageDialog}
            >
              <MessageSquare size={24} />
              Nachricht
            </button>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      <Dialog
        open={pauseDialogOpen}
        setOpen={setPauseDialogOpen}
        title={pause ? "Bildschirme pausiert" : "Bildschirme pausieren"}
        buttons={[
          pause ? (
            <Button variant={Variants.SUCCESS} onClick={endPause}>
              Fortsetzen
            </Button>
          ) : (
            <Button variant={Variants.PRIMARY} onClick={startPause}>
              Pausieren
            </Button>
          ),
        ]}
      >
        <Text variant="body" color="neutral-100">
          {pause
            ? "Die Bildschirme deiner Schüler*innen sind aktuell pausiert."
            : "Pausiert alle Schüler*innen Bildschirme. Nur du kannst es wieder aufheben."}
        </Text>
      </Dialog>

      <Dialog
        open={messageDialogOpen}
        setOpen={setMessageDialogOpen}
        title="Nachricht an die Klasse"
        buttons={[
          // On send the button itself flips to a green success state; the dialog
          // then auto-closes (see the effect above), so there's nothing to end.
          messageSent ? (
            <Button
              variant={Variants.SUCCESS}
              onClick={() => setMessageDialogOpen(false)}
            >
              Nachricht gesendet
            </Button>
          ) : (
            <Button
              variant={Variants.PRIMARY}
              onClick={sendMessage}
              disabled={messageText.trim() === ""}
            >
              An alle senden
            </Button>
          ),
        ]}
      >
        <div className={styles.messageStack}>
          <Text variant="body" color="neutral-100">
            Deine Nachricht erscheint auf allen Bildschirmen der Schüler*innen.
          </Text>
          <Textarea
            name="classroom-message"
            label="Nachricht"
            theme="dark"
            value={messageText}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
              setMessageText(e.target.value)
            }
            width100
            placeholder="z. B. Super gemacht! Ihr habt noch 5 Minuten für die Recherche."
            fullHeight={false}
          />
        </div>
      </Dialog>
    </>
  );
};
